import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import PostureComplianceStatus from '../../SecurityPosture/PostureComplianceStatus/PostureComplianceStatus'
import './ComplianceAuditSummary.module.scss'

// Legend rows, in draw order. Each maps a summary count to the shared posture-status label;
// the dot colour comes from the matching `legendDot--<key>` modifier in the stylesheet.
const LEGEND = [
  { key: 'compliant', labelId: 'SECURE.POSTURE.CONFIGURED' },
  { key: 'partial', labelId: 'SECURE.POSTURE.PARTIALLY_CONFIGURED' },
  { key: 'nonCompliant', labelId: 'SECURE.POSTURE.NOT_CONFIGURED' },
  { key: 'resolved', labelId: 'SECURE.POSTURE.RESOLVED' },
  { key: 'notMeasured', labelId: 'SECURE.POSTURE.NOT_MEASURED' },
]

const summaryShape = PropTypes.shape({
  compliant: PropTypes.number,
  partial: PropTypes.number,
  nonCompliant: PropTypes.number,
  resolved: PropTypes.number,
  notMeasured: PropTypes.number,
  total: PropTypes.number,
  percentage: PropTypes.number,
})

const propTypes = {
  /** Tenant-wide Microsoft Secure Score. Omit to hide the left card. */
  secureScore: PropTypes.shape({
    percentage: PropTypes.number,
    current: PropTypes.number,
    total: PropTypes.number,
  }),
  /**
   * Right-card title — the exported scope (assessment name, "All saved assessments", "All
   * posture checks"). Omit together with `summary` for the Secure Score scope, where only
   * the Secure Score card is shown.
   */
  scopeLabel: PropTypes.node,
  /** Aggregate compliance for the exported scope (from `buildComplianceSummary`). */
  summary: summaryShape,
  /** Assessment names to list as chips — used for the "all saved assessments" scope. */
  assessmentNames: PropTypes.arrayOf(PropTypes.string),
  /** Class to extend or override default styles. */
  className: PropTypes.string,
}

function formatPercent(value) {
  return `${Math.round(value ?? 0)}%`
}

function ScoreBar({ percentage }) {
  return (
    <progress
      className="Uic-ComplianceAuditSummary-scoreBar"
      max={100}
      value={Math.min(Math.max(percentage ?? 0, 0), 100)}
    />
  )
}

ScoreBar.propTypes = { percentage: PropTypes.number }

/**
 * ComplianceAuditSummary — the grounded header for the Compliance Audit export: a
 * tenant-wide Microsoft Secure Score card and a scope-summary card (the selected
 * assessment, all saved assessments, or all posture checks). Mirrors the in-app
 * AssessmentCard / StatusBadge visual language. Tokenized for light + dark.
 */
function ComplianceAuditSummary({
  secureScore = null,
  scopeLabel = null,
  summary = null,
  assessmentNames = null,
  className = '',
}) {
  const intl = useIntl()

  const showScope = summary != null && scopeLabel != null
  const showAssessmentList = Array.isArray(assessmentNames) && assessmentNames.length > 0

  return (
    <div className={clsx('Uic-ComplianceAuditSummary-root', className)}>
      {secureScore && (
        <section className="Uic-ComplianceAuditSummary-card">
          <div className="Uic-ComplianceAuditSummary-head">
            <div className="Uic-ComplianceAuditSummary-title">
              {intl.formatMessage({ id: 'COMPLIANCE_AUDIT.SUMMARY.SECURE_SCORE_TITLE' })}
            </div>
            <div className="Uic-ComplianceAuditSummary-value">{formatPercent(secureScore.percentage)}</div>
            <div className="Uic-ComplianceAuditSummary-sub">
              {intl.formatMessage(
                { id: 'COMPLIANCE_AUDIT.SUMMARY.POINTS' },
                { current: secureScore.current, total: secureScore.total },
              )}
            </div>
          </div>
          <ScoreBar percentage={secureScore.percentage} />
        </section>
      )}

      {showScope && (
        <section className="Uic-ComplianceAuditSummary-card">
          <div className="Uic-ComplianceAuditSummary-head">
            <div className="Uic-ComplianceAuditSummary-title">{scopeLabel}</div>
            <div className="Uic-ComplianceAuditSummary-value">{formatPercent(summary.percentage)}</div>
          </div>

          <div className="Uic-ComplianceAuditSummary-bar">
            <PostureComplianceStatus
              configured={summary.compliant}
              partiallyConfigured={summary.partial}
              notConfigured={summary.nonCompliant}
              resolved={summary.resolved}
              notMeasured={summary.notMeasured}
              showTitle={false}
              showLegendFooter={false}
            />
          </div>

          <div className="Uic-ComplianceAuditSummary-legend">
            {LEGEND.map(({ key, labelId }) => (
              <span key={key} className="Uic-ComplianceAuditSummary-legendItem">
                <span className={`Uic-ComplianceAuditSummary-legendDot Uic-ComplianceAuditSummary-legendDot--${key}`} />
                <span className="Uic-ComplianceAuditSummary-legendLabel">{intl.formatMessage({ id: labelId })}</span>
                <span className="Uic-ComplianceAuditSummary-legendValue">{summary[key] ?? 0}</span>
              </span>
            ))}
          </div>

          {/* Assessment chips sit at the bottom so a long list never pushes the distribution
              bar out of line with the Secure Score bar in the adjacent card. */}
          {showAssessmentList && (
            <div className="Uic-ComplianceAuditSummary-chips">
              {assessmentNames.map((name) => (
                <span key={name} className="Uic-ComplianceAuditSummary-chip">
                  {name}
                </span>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}

ComplianceAuditSummary.propTypes = propTypes

export default ComplianceAuditSummary
